import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  TextField,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  InputAdornment,
  Typography,
  Box,
  Chip,
  Divider,
  IconButton,
  Paper,
  alpha,
  useTheme,
} from '@mui/material';
import {
  Search as SearchIcon,
  Close as CloseIcon,
  Dashboard as DashboardIcon,
  Description as DocumentIcon,
  Receipt as TransactionIcon,
  Security as ComplianceIcon,
  Edit as MarkerIcon,
  AdminPanelSettings as AdminIcon,
  Person as ProfileIcon,
  Settings as SettingsIcon,
  Help as HelpIcon,
  Logout as LogoutIcon,
  ArrowForward as ArrowIcon,
  History as HistoryIcon,
  FileUpload as UploadIcon,
  Assessment as ReportsIcon,
  Language as LanguageIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { useTranslation } from '../store/languageStore';

interface SearchItem {
  id: string;
  title: string;
  description?: string;
  icon: React.ReactNode;
  category: 'navigation' | 'action' | 'recent' | 'help';
  action: () => void;
  keywords: string[];
  shortcut?: string;
}

interface QuickSearchProps {
  open: boolean;
  onClose: () => void;
}

export const QuickSearch: React.FC<QuickSearchProps> = ({ open, onClose }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [recentSearches, setRecentSearches] = useState<string[]>([]);

  // Load recent searches from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('recentSearches');
    if (saved) {
      setRecentSearches(JSON.parse(saved));
    }
  }, []);

  const searchItems: SearchItem[] = useMemo(() => [
    // Navigation items
    {
      id: 'dashboard',
      title: t('navigation.dashboard') as string,
      description: 'View your main dashboard',
      icon: <DashboardIcon />,
      category: 'navigation',
      action: () => {
        navigate('/process');
        onClose();
      },
      keywords: ['home', 'main', 'overview', 'dashboard'],
      shortcut: '⌘D',
    },
    {
      id: 'process',
      title: t('navigation.processDocuments') as string,
      description: 'Upload and process new documents',
      icon: <DocumentIcon />,
      category: 'navigation',
      action: () => {
        navigate('/process');
        onClose();
      },
      keywords: ['upload', 'document', 'process', 'ocr', 'extract'],
      shortcut: '⌘P',
    },
    {
      id: 'transactions',
      title: t('navigation.transactionHistory') as string,
      description: 'View all your transactions',
      icon: <TransactionIcon />,
      category: 'navigation',
      action: () => {
        navigate('/transactions');
        onClose();
      },
      keywords: ['history', 'transactions', 'records', 'logs'],
      shortcut: '⌘T',
    },
    {
      id: 'compliance',
      title: t('navigation.compliance') as string,
      description: 'Compliance checking and reports',
      icon: <ComplianceIcon />,
      category: 'navigation',
      action: () => {
        navigate('/compliance');
        onClose();
      },
      keywords: ['compliance', 'sanctions', 'aml', 'regulations'],
      shortcut: '⌘C',
    },
    {
      id: 'magic-marker',
      title: 'Magic Marker',
      description: 'Smart document annotation',
      icon: <MarkerIcon />,
      category: 'navigation',
      action: () => {
        navigate('/magic-marker');
        onClose();
      },
      keywords: ['annotate', 'mark', 'highlight', 'magic'],
      shortcut: '⌘M',
    },

    // Actions
    {
      id: 'upload-document',
      title: 'Upload New Document',
      description: 'Start processing a new document',
      icon: <UploadIcon />,
      category: 'action',
      action: () => {
        navigate('/process');
        onClose();
        setTimeout(() => {
          const uploadButton = document.querySelector('input[type="file"]') as HTMLInputElement;
          uploadButton?.click();
        }, 100);
      },
      keywords: ['new', 'upload', 'add', 'file'],
      shortcut: '⌘N',
    },
    {
      id: 'generate-report',
      title: 'Generate Report',
      description: 'Create compliance or transaction report',
      icon: <ReportsIcon />,
      category: 'action',
      action: () => {
        navigate('/reports');
        onClose();
      },
      keywords: ['report', 'export', 'generate', 'pdf', 'excel'],
    },
    {
      id: 'change-language',
      title: 'Change Language',
      description: 'Switch application language',
      icon: <LanguageIcon />,
      category: 'action',
      action: () => {
        const langSelector = document.querySelector('[data-testid="language-selector"]') as HTMLElement;
        langSelector?.click();
        onClose();
      },
      keywords: ['language', 'locale', 'translate', 'english', 'german', 'french', 'spanish'],
    },

    // User actions
    {
      id: 'profile',
      title: t('navigation.profile') as string,
      description: 'View and edit your profile',
      icon: <ProfileIcon />,
      category: 'action',
      action: () => {
        navigate('/profile');
        onClose();
      },
      keywords: ['profile', 'account', 'user', 'settings'],
      shortcut: '⌘U',
    },
    {
      id: 'settings',
      title: 'Settings',
      description: 'Application settings',
      icon: <SettingsIcon />,
      category: 'action',
      action: () => {
        navigate('/settings');
        onClose();
      },
      keywords: ['settings', 'preferences', 'config', 'options'],
    },
    {
      id: 'logout',
      title: t('auth.logout') as string,
      description: 'Sign out of your account',
      icon: <LogoutIcon />,
      category: 'action',
      action: () => {
        logout();
        onClose();
      },
      keywords: ['logout', 'signout', 'exit', 'leave'],
      shortcut: '⌘⇧L',
    },

    // Help
    {
      id: 'help',
      title: 'Help & Documentation',
      description: 'View help and documentation',
      icon: <HelpIcon />,
      category: 'help',
      action: () => {
        window.open('/help', '_blank');
        onClose();
      },
      keywords: ['help', 'docs', 'documentation', 'guide', 'tutorial'],
      shortcut: '⌘H',
    },
    {
      id: 'shortcuts',
      title: 'Keyboard Shortcuts',
      description: 'View all keyboard shortcuts',
      icon: <HelpIcon />,
      category: 'help',
      action: () => {
        // Trigger shortcuts dialog
        const event = new KeyboardEvent('keydown', { key: '?', shiftKey: true });
        window.dispatchEvent(event);
        onClose();
      },
      keywords: ['shortcuts', 'keyboard', 'hotkeys', 'keys'],
      shortcut: '?',
    },
  ], [navigate, onClose, logout, t]);

  // Add admin items if user is admin
  const allItems = useMemo(() => {
    const items = [...searchItems];

    if (user?.is_admin) {
      items.push({
        id: 'admin',
        title: 'Admin Dashboard',
        description: 'Manage users and system',
        icon: <AdminIcon />,
        category: 'navigation',
        action: () => {
          navigate('/admin');
          onClose();
        },
        keywords: ['admin', 'manage', 'users', 'system'],
        shortcut: '⌘⇧A',
      });
    }

    return items;
  }, [searchItems, user, navigate, onClose]);

  // Filter items based on search query
  const filteredItems = useMemo(() => {
    if (!searchQuery.trim()) {
      // Show recent items and common actions when no search
      return allItems.filter(item =>
        ['dashboard', 'process', 'transactions', 'upload-document'].includes(item.id)
      );
    }

    const query = searchQuery.toLowerCase();
    return allItems.filter(item => {
      const inTitle = item.title?.toLowerCase()?.includes(query) || false;
      const inDescription = item.description?.toLowerCase()?.includes(query) || false;
      const inKeywords = item.keywords?.some(k => k?.toLowerCase()?.includes(query)) || false;
      return inTitle || inDescription || inKeywords;
    });
  }, [searchQuery, allItems]);

  // Handle keyboard navigation
  const handleKeyDown = useCallback((event: React.KeyboardEvent) => {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        setSelectedIndex(prev => (prev + 1) % filteredItems.length);
        break;
      case 'ArrowUp':
        event.preventDefault();
        setSelectedIndex(prev => (prev - 1 + filteredItems.length) % filteredItems.length);
        break;
      case 'Enter':
        event.preventDefault();
        if (filteredItems[selectedIndex]) {
          // Save to recent searches
          const recent = [searchQuery, ...recentSearches.filter(s => s !== searchQuery)].slice(0, 5);
          setRecentSearches(recent);
          localStorage.setItem('recentSearches', JSON.stringify(recent));

          filteredItems[selectedIndex].action();
        }
        break;
      case 'Escape':
        onClose();
        break;
    }
  }, [filteredItems, selectedIndex, searchQuery, recentSearches, onClose]);

  // Reset selected index when search changes
  useEffect(() => {
    setSelectedIndex(0);
  }, [searchQuery]);

  // Reset when dialog opens
  useEffect(() => {
    if (open) {
      setSearchQuery('');
      setSelectedIndex(0);
    }
  }, [open]);

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'navigation':
        return 'primary';
      case 'action':
        return 'secondary';
      case 'recent':
        return 'default';
      case 'help':
        return 'info';
      default:
        return 'default';
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          position: 'fixed',
          top: '20%',
          borderRadius: 2,
          maxHeight: '60vh',
        },
      }}
    >
      <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <TextField
            fullWidth
            placeholder="Search for anything..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            autoFocus
            variant="outlined"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
              endAdornment: searchQuery && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearchQuery('')}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
              sx: {
                fontSize: '1.1rem',
                '& fieldset': { border: 'none' },
              },
            }}
          />
        </Box>

        <Box sx={{ flex: 1, overflowY: 'auto', maxHeight: '400px' }}>
          {filteredItems.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="textSecondary">
                No results found for "{searchQuery}"
              </Typography>
            </Box>
          ) : (
            <List sx={{ py: 1 }}>
              {filteredItems.map((item, index) => (
                <ListItemButton
                  key={item.id}
                  selected={index === selectedIndex}
                  onClick={() => {
                    item.action();
                  }}
                  onMouseEnter={() => setSelectedIndex(index)}
                  sx={{
                    mx: 1,
                    borderRadius: 1,
                    '&.Mui-selected': {
                      backgroundColor: alpha(theme.palette.primary.main, 0.1),
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.15),
                      },
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body1">{item.title}</Typography>
                        <Chip
                          label={item.category}
                          size="small"
                          color={getCategoryColor(item.category)}
                          sx={{ height: 20 }}
                        />
                      </Box>
                    }
                    secondary={item.description}
                  />
                  {item.shortcut && (
                    <Box
                      sx={{
                        px: 1,
                        py: 0.5,
                        borderRadius: 1,
                        backgroundColor: theme.palette.grey[200],
                        fontFamily: 'monospace',
                        fontSize: '0.75rem',
                      }}
                    >
                      {item.shortcut}
                    </Box>
                  )}
                </ListItemButton>
              ))}
            </List>
          )}
        </Box>

        <Box sx={{ p: 1, borderTop: 1, borderColor: 'divider', display: 'flex', gap: 2 }}>
          <Typography variant="caption" color="textSecondary">
            <kbd>↑↓</kbd> Navigate
          </Typography>
          <Typography variant="caption" color="textSecondary">
            <kbd>↵</kbd> Select
          </Typography>
          <Typography variant="caption" color="textSecondary">
            <kbd>ESC</kbd> Close
          </Typography>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default QuickSearch;